import type { Node as ProseMirrorNode } from '@milkdown/kit/prose/model';

import type {
  ReviewSuggestion,
  ReviewSuggestionResolveOutcome,
  ReviewSuggestionUnresolvedOutcome,
  ReviewSuggestionsResolveSummary,
} from '../../types';
import { looksLikeBlockSuggestion } from './decorations';
import { normalizeAnchors, normalizeFlatTextChunk } from './markdownAnchor';
import type { ResolvedReviewSuggestionRange } from './types';

/**
 * Maps review-suggestion anchors (`contextPrefix` / `originalText` / `contextSuffix`)
 * to ProseMirror positions by searching a flattened copy of the live document.
 *
 * Matching pipeline (see `locateAnchor`):
 * - Build `flat` from text nodes (no block separators; see `flattenDoc`).
 * - Normalize API anchors via `normalizeAnchors` (markdown stripped, newlines removed).
 * - Try strict substring match, then flexible token patterns, then suffix variants for inserts.
 * - Require a unique match; map flat offsets back through `offsetToPos`.
 */

/**
 * Flatten the doc into a single string and a parallel `offsetToPos` array that
 * maps each flat-text character offset back to a PM document position.
 *
 * Block structure (headings, list items, table cells, blockquotes) and inline
 * marks (bold, italic, etc.) are erased — only text node content survives, with
 * no separator inserted between blocks. Adjacent blocks therefore appear back-to-back
 * in `flat` (e.g. `format?Yes`, `style.Troubleshooting`). Each chunk is passed through
 * `normalizeFlatTextChunk` so anchor matching uses the same punctuation rules
 * as `normalizeAnchors`.
 */
function flattenDoc(doc: ProseMirrorNode): { flat: string; offsetToPos: number[] } {
  const offsetToPos: number[] = [];
  let flat = '';

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return true;

    const chunk = normalizeFlatTextChunk(node.text);
    for (let i = 0; i < chunk.length; i += 1) offsetToPos.push(pos + i);
    flat += chunk;

    return true;
  });

  return { flat, offsetToPos };
}

type LocatedRange = { from: number; to: number };
type LocatedAnchor =
  | ({ outcome: 'resolved' | 'resolved_fallback' } & LocatedRange)
  | { outcome: 'not_found' | 'ambiguous' | 'empty_anchor' };

type ResolveSummaryCallback = (summary: ReviewSuggestionsResolveSummary) => void;

function unresolvedReason(outcome: ReviewSuggestionUnresolvedOutcome): string {
  switch (outcome) {
    case 'not_found':
      return 'Anchor text was not found in the document.';
    case 'ambiguous':
      return 'Anchor text matched multiple locations in the document.';
    case 'empty_anchor':
      return 'Suggestion did not include enough anchor text to resolve.';
    default:
      return outcome satisfies never;
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Exact substring search; returns start index only when `needle` appears once in `flat`. */
function findUnique(flat: string, needle: string): number | null {
  if (!needle) return null;
  const first = flat.indexOf(needle);
  if (first === -1) return null;
  if (flat.indexOf(needle, first + 1) !== -1) return null;

  return first;
}

/** Split anchor text on whitespace for flexible matching (each token must still appear in order). */
function anchorTokens(text: string): string[] {
  return text.split(/\s+/).filter((part) => part.length > 0);
}

/** Join escaped tokens with `\\s*` so gaps between words may be absent or wider in `flat`. */
function tokensToFlexiblePattern(tokens: string[]): string | null {
  if (tokens.length === 0) return null;

  return tokens.map(escapeRegExp).join('\\s*');
}

/**
 * Like `findUnique`, but matches `needle` token-by-token allowing flexible whitespace
 * between tokens (`\s*`: zero or more). Returns the start index only when the pattern
 * matches exactly once.
 */
function findUniqueFlexibleWhitespace(flat: string, needle: string): number | null {
  const pattern = tokensToFlexiblePattern(anchorTokens(needle));
  if (!pattern) return null;

  const re = new RegExp(pattern, 'g');
  let firstIndex: number | null = null;
  let matchCount = 0;
  let execResult: RegExpExecArray | null;

  while ((execResult = re.exec(flat)) !== null) {
    matchCount += 1;
    if (matchCount === 1) firstIndex = execResult.index;
    if (matchCount > 1) return null;
  }

  return matchCount === 1 ? firstIndex : null;
}

/** Captured lengths in `flat` for each anchor segment (may differ from needle lengths when `\\s*` collapses). */
type FlexibleThreePartMatch = {
  matchIndex: number;
  prefixSpan: number;
  originalSpan: number;
};

/**
 * Flexible match of prefix + original + suffix with capture groups so `from`/`to`
 * use matched span lengths in `flat`, not needle string lengths.
 */
function findUniqueFlexibleThreePart(flat: string, prefix: string, original: string, suffix: string): FlexibleThreePartMatch | null {
  const segments: string[] = [];
  const partMeta: { kind: 'prefix' | 'original' | 'suffix'; groupIndex: number }[] = [];
  let groupIndex = 0;

  const addPart = (kind: 'prefix' | 'original' | 'suffix', text: string) => {
    const pattern = tokensToFlexiblePattern(anchorTokens(text));
    if (!pattern) return;
    if (segments.length > 0) segments.push('\\s*');
    groupIndex += 1;
    segments.push(`(${pattern})`);
    partMeta.push({ kind, groupIndex });
  };

  addPart('prefix', prefix);
  addPart('original', original);
  addPart('suffix', suffix);

  if (segments.length === 0) return null;

  const re = new RegExp(segments.join(''), 'g');
  let firstMatch: RegExpExecArray | null = null;
  let matchCount = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(flat)) !== null) {
    matchCount += 1;
    if (matchCount === 1) firstMatch = match;
    if (matchCount > 1) return null;
  }

  if (!firstMatch) return null;

  let prefixSpan = 0;
  let originalSpan = 0;
  for (const { kind, groupIndex: gi } of partMeta) {
    const captured = firstMatch[gi] ?? '';
    if (kind === 'prefix') prefixSpan = captured.length;
    if (kind === 'original') originalSpan = captured.length;
  }

  return {
    matchIndex: firstMatch.index,
    prefixSpan,
    originalSpan,
  };
}

/**
 * Suffix strings to try for insert matching when the editor omits ordered-list markers.
 * Returns one or two entries: [original, optional copy with first `N. ` removed after a letter].
 * Example: `Instructions1. Click` → also try `InstructionsClick` (PM list items often lack `1.` in text).
 */
function suffixAnchorVariants(suffix: string): string[] {
  const variants = [suffix];
  const withoutListMarker = suffix.replace(/([A-Za-z])\d+\.\s+/, '$1');
  if (withoutListMarker !== suffix) variants.push(withoutListMarker);

  return variants;
}

/**
 * `prefix` immediately followed by `suffix` in `flat` (with optional whitespace between).
 * Insertion point is the end of the matched prefix. Caller must pass non-empty `prefix`.
 */
function findUniqueAdjacentPrefixSuffixMatch(flat: string, prefix: string, suffix: string): LocatedRange | null {
  const pattern = `(${escapeRegExp(prefix)})\\s*(?:${escapeRegExp(suffix)})`;
  const re = new RegExp(pattern, 'g');
  let firstMatch: RegExpExecArray | null = null;
  let matchCount = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(flat)) !== null) {
    matchCount += 1;
    if (matchCount === 1) firstMatch = match;
    if (matchCount > 1) return null;
  }

  if (matchCount !== 1 || !firstMatch?.[1]) return null;

  const from = firstMatch.index + firstMatch[1].length;

  return { from, to: from };
}

/**
 * Second-chance insert locator after `resolveStrictOrFlexible` fails.
 *
 * - Empty `prefix`: try each `suffixAnchorVariants` entry with flexible whitespace matching.
 * - Non-empty `prefix`: try each suffix variant where `prefix` and `suffix` abut in `flat`.
 *
 * Variants are tried in order; the first unique match wins.
 */
function findUniqueAdjacentPrefixSuffix(flat: string, prefix: string, suffix: string): LocatedRange | null {
  if (!suffix) return null;

  if (!prefix) {
    for (const suffixVariant of suffixAnchorVariants(suffix)) {
      const pos = findUniqueFlexibleWhitespace(flat, suffixVariant);
      if (pos !== null) return { from: pos, to: pos };
    }

    return null;
  }

  for (const suffixVariant of suffixAnchorVariants(suffix)) {
    const adjacent = findUniqueAdjacentPrefixSuffixMatch(flat, prefix, suffixVariant);
    if (adjacent) return adjacent;
  }

  return null;
}

/**
 * Primary locator: strict `indexOf(needle)` then flexible three-part regex.
 * Uses the original normalized anchor strings (no `suffixAnchorVariants` here).
 * Returns a range covering `original` only; for inserts `original` is empty so `from === to`.
 */
function resolveStrictOrFlexible(flat: string, prefix: string, original: string, suffix: string): LocatedAnchor | null {
  const strictNeedle = prefix + original + suffix;
  const strictFirst = flat.indexOf(strictNeedle);
  if (strictFirst !== -1 && flat.indexOf(strictNeedle, strictFirst + 1) === -1) {
    return {
      outcome: 'resolved',
      from: strictFirst + prefix.length,
      to: strictFirst + prefix.length + original.length,
    };
  }

  const flexMatch = findUniqueFlexibleThreePart(flat, prefix, original, suffix);
  if (flexMatch === null) return null;

  return {
    outcome: 'resolved',
    from: flexMatch.matchIndex + flexMatch.prefixSpan,
    to: flexMatch.matchIndex + flexMatch.prefixSpan + flexMatch.originalSpan,
  };
}

/**
 * Locate the suggestion's anchor in flat text (already normalized via
 * `normalizeAnchors` on the caller side).
 *
 * Replace/delete (`original` non-empty), drift-tolerant:
 * 1. `resolveStrictOrFlexible` on `prefix + original + suffix` → `resolved`.
 * 2. One-sided fallbacks — `original + suffix` (prefix drifted) and
 *    `prefix + original` (suffix drifted), each unique in `flat`.
 *    - One match → `resolved_fallback`; both match different positions → `ambiguous`.
 * 3. Otherwise `not_found`, or `ambiguous` if the strict needle matched twice.
 *
 * Inserts (`original` empty):
 * 1. `resolveStrictOrFlexible` on original `prefix + suffix` (strict, then flexible three-part).
 * 2. If that fails, `findUniqueAdjacentPrefixSuffix` (suffix variants + flexible whitespace).
 * 3. Does not use one-sided fallbacks (prefix-only / suffix-only is usually ambiguous).
 * 4. Otherwise `not_found` or `ambiguous`.
 *
 * When the strict needle appears more than once in `flat`, steps 1–2 are skipped
 * (fail-closed). Flexible matching cannot disambiguate a duplicated literal needle;
 * replace/delete then use one-sided fallbacks; inserts return `ambiguous` or `not_found`.
 *
 * Callers pass normalized prefix/suffix/original; markdown stripping happens in
 * `normalizeAnchors` before this function runs.
 */
function locateAnchor(flat: string, prefix: string, original: string, suffix: string): LocatedAnchor {
  const strictNeedle = prefix + original + suffix;
  if (!strictNeedle) return { outcome: 'empty_anchor' };

  const strictFirst = flat.indexOf(strictNeedle);
  const strictAmbiguous = strictFirst !== -1 && flat.indexOf(strictNeedle, strictFirst + 1) !== -1;

  if (!strictAmbiguous) {
    const resolved = resolveStrictOrFlexible(flat, prefix, original, suffix);
    if (resolved) return resolved;

    if (original.length === 0) {
      const adjacent = findUniqueAdjacentPrefixSuffix(flat, prefix, suffix);
      if (adjacent) return { outcome: 'resolved', ...adjacent };
    }
  }

  if (original.length === 0) {
    return strictAmbiguous ? { outcome: 'ambiguous' } : { outcome: 'not_found' };
  }

  const suffixSidePos = findUnique(flat, original + suffix);
  const prefixSidePos = findUnique(flat, prefix + original);

  const suffixSideRange: LocatedRange | null = suffixSidePos !== null ? { from: suffixSidePos, to: suffixSidePos + original.length } : null;
  const prefixSideRange: LocatedRange | null =
    prefixSidePos !== null ? { from: prefixSidePos + prefix.length, to: prefixSidePos + prefix.length + original.length } : null;

  if (suffixSideRange && prefixSideRange) return { outcome: 'ambiguous' };
  if (suffixSideRange) return { outcome: 'resolved_fallback', ...suffixSideRange };
  if (prefixSideRange) return { outcome: 'resolved_fallback', ...prefixSideRange };

  return strictAmbiguous ? { outcome: 'ambiguous' } : { outcome: 'not_found' };
}

/** Block-level inserts after a heading should render outside the heading node, not inside its text. */
function headingBoundaryInsertPosition(doc: ProseMirrorNode, pos: number, replacementText: string): number | null {
  if (!looksLikeBlockSuggestion(replacementText)) return null;

  try {
    const $pos = doc.resolve(pos);
    if ($pos.parent.type.name !== 'heading') return null;
    if ($pos.parentOffset !== $pos.parent.content.size) return null;

    return $pos.after($pos.depth);
  } catch {
    return null;
  }
}

/** Map a flat-text offset from `locateAnchor` to a PM position for a zero-width insert decoration. */
function resolveInsertPosition(doc: ProseMirrorNode, offsetToPos: number[], offset: number, textLength: number, replacementText: string) {
  if (offsetToPos.length === 0) return null;
  if (offset <= 0) return offsetToPos[0];
  if (offset >= textLength) {
    const lastPos = offsetToPos[offsetToPos.length - 1];

    return lastPos + 1;
  }

  // Prefer "right after the previous char" over "at the next text node" so
  // inserts at block boundaries land inside the preceding block (where the
  // suggestion's anchor phrase ends) rather than at the start of the next
  // block. Within a single text node both options are the same position; they
  // only diverge at block boundaries, where picking the successor would push
  // the widget out of the containing <td>/<li>/<blockquote>.
  const previousOffset = offsetToPos[offset - 1];
  if (previousOffset != null) {
    const pos = previousOffset + 1;
    const headingBoundaryPos = headingBoundaryInsertPosition(doc, pos, replacementText);

    return headingBoundaryPos ?? pos;
  }

  return offsetToPos[offset] ?? null;
}

/**
 * Resolves anchor-based suggestions to PM positions against `doc`. The
 * conversion is one-shot: callers should map returned ranges forward across
 * future edits instead of re-resolving against later docs (otherwise drift
 * compounds and decorations slide off their live text).
 *
 * Anchor fields are normalized via `normalizeAnchors` before
 * `locateAnchor` runs on the flattened document string.
 *
 * `onOutcome` is invoked exactly once per input suggestion regardless of
 * whether it resolves, so hosts can emit a counter keyed by outcome.
 */
export function resolveReviewSuggestionRanges(
  doc: ProseMirrorNode,
  reviewSuggestions: ReviewSuggestion[] | undefined,
  onOutcome?: (suggestionId: string, outcome: ReviewSuggestionResolveOutcome) => void,
  onSummary?: ResolveSummaryCallback,
): ResolvedReviewSuggestionRange[] {
  const suggestions = reviewSuggestions ?? [];
  if (suggestions.length === 0) {
    onSummary?.({
      totalCount: 0,
      resolvedCount: 0,
      unresolvedCount: 0,
      unresolved: [],
    });

    return [];
  }

  const { flat, offsetToPos } = flattenDoc(doc);
  const resolved: ResolvedReviewSuggestionRange[] = [];
  const unresolved: ReviewSuggestionsResolveSummary['unresolved'] = [];

  for (const suggestion of suggestions) {
    const { originalText, contextPrefix, contextSuffix } = normalizeAnchors(
      suggestion.originalText,
      suggestion.contextPrefix,
      suggestion.contextSuffix,
    );
    const located = locateAnchor(flat, contextPrefix, originalText, contextSuffix);
    onOutcome?.(suggestion.id, located.outcome);
    if (located.outcome !== 'resolved' && located.outcome !== 'resolved_fallback') {
      unresolved.push({
        id: suggestion.id,
        outcome: located.outcome,
        reason: unresolvedReason(located.outcome),
      });
      continue;
    }

    if (suggestion.opType === 'insert') {
      const insertPos = resolveInsertPosition(doc, offsetToPos, located.from, flat.length, suggestion.proposedText);
      if (insertPos == null) continue;

      resolved.push({
        id: suggestion.id,
        from: insertPos,
        to: insertPos,
        opType: suggestion.opType,
        replacementText: suggestion.proposedText,
      });
      continue;
    }

    const startPos = offsetToPos[located.from];
    const endPos = offsetToPos[located.to - 1];
    if (startPos == null || endPos == null) continue;

    resolved.push({
      id: suggestion.id,
      from: startPos,
      to: endPos + 1,
      opType: suggestion.opType,
      replacementText: suggestion.proposedText,
    });
  }

  const ordered = resolved
    .map((range, index) => ({ range, index }))
    .sort((a, b) => {
      if (a.range.from !== b.range.from) return a.range.from - b.range.from;
      if (a.range.to !== b.range.to) return a.range.to - b.range.to;

      return a.index - b.index;
    })
    .map(({ range }) => range);

  onSummary?.({
    totalCount: suggestions.length,
    resolvedCount: ordered.length,
    unresolvedCount: unresolved.length,
    unresolved,
  });

  return ordered;
}
